/**
 * Shared tag pill color mapping for both the Composer (pre-send preview)
 * and the TeamLogStream (rendered messages). Using one source keeps the
 * visual identity consistent — what Travis saw as a red "blocker" pill
 * when composing renders as the same red pill on his posted message.
 *
 * Colors come from the existing `--tag-*` tokens in src/index.css. If
 * the token set evolves (planner's EFISSUE-786 two-tier model, spec
 * color revisions), update this file + index.css together; both the
 * Composer and the stream read from this map.
 */

export interface TagPillStyle {
  color: string;
  bg: string;
  border: string;
}

const KNOWN_TAGS = [
  "blocker",
  "needs-review",
  "pre-flight",
  "for-review",
  "build-complete",
  "ready-to-stage",
  "bundle-complete",
  "pushed",
  "approved",
  "ritual-complete",
  "reference",
  "question",
  // Sprint 5 C1 (`hfZpdLwISuYwQhK2rJT3`) — projects merged into pinned
  // filters. Each project tag gets a distinct color dot in the sidebar.
  "relay",
  "juju",
] as const;

export type KnownTagName = typeof KNOWN_TAGS[number];

/** Neutral fallback pill for Tier 2 / unknown tags. */
const NEUTRAL: TagPillStyle = {
  color: "var(--text2)",
  bg: "var(--surface2)",
  border: "1px solid var(--border)",
};

/**
 * Return the pill style for a tag name. Unknown tags render neutral.
 * Matches the existing TAG_GROUPS mapping in TeamLogStream + the
 * Composer's tokenMap so the pill colors stay consistent.
 */
export function tagPillStyle(tag: string): TagPillStyle {
  switch (tag) {
    case "blocker":
      return {
        color: "var(--tag-blocker)",
        bg: "var(--tag-blocker-bg)",
        border: "1px solid var(--tag-blocker)",
      };
    case "needs-review":
    case "pre-flight":
    case "for-review":
      return {
        color: "var(--tag-review)",
        bg: "var(--tag-review-bg)",
        border: "1px solid var(--tag-review)",
      };
    case "build-complete":
    case "ready-to-stage":
    case "bundle-complete":
    case "pushed":
      return {
        color: "var(--tag-ready)",
        bg: "var(--tag-ready-bg)",
        border: "1px solid var(--tag-ready)",
      };
    case "approved":
    case "ritual-complete":
      return {
        color: "var(--tag-approved)",
        bg: "var(--tag-approved-bg)",
        border: "1px solid var(--tag-approved)",
      };
    case "reference":
      return {
        color: "var(--tag-reference)",
        bg: "var(--tag-reference-bg)",
        border: "1px solid var(--tag-reference)",
      };
    case "question":
      return {
        color: "var(--accent)",
        bg: "var(--surface2)",
        border: "1px solid var(--accent)",
      };
    // Sprint 5 C1 (`hfZpdLwISuYwQhK2rJT3`) — project filter colors.
    // Distinct hues from the existing tag groups so the sidebar reads
    // "● relay" + "● juju" + "● blocker" with three different dots.
    case "relay":
      return {
        color: "oklch(0.62 0.18 252)",
        bg: "oklch(0.62 0.18 252 / 0.12)",
        border: "1px solid oklch(0.62 0.18 252)",
      };
    case "juju":
      return {
        color: "oklch(0.62 0.16 80)",
        bg: "oklch(0.62 0.16 80 / 0.12)",
        border: "1px solid oklch(0.62 0.16 80)",
      };
    default:
      return NEUTRAL;
  }
}

export { KNOWN_TAGS };
